/*
 * Project: bellcloud
 * File: apps/frontend/src/components/common/HierarchicalSelectorContainer.tsx
 * Description: Container component for HierarchicalSelector with business logic
 */

import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { HierarchicalSelector, type SelectionDepth } from './HierarchicalSelector';
import { useAuthStore } from '@frontend/src/store/authStore';
import { useSelectionStore } from '@frontend/src/store/selectionStore';
import { useCompanySiteScope } from '@frontend/src/hooks/useCompanySiteScope';
import { useGetSiteDevices } from '@frontend/src/api/device/useDevice';
import { getDepthForPath } from '@frontend/src/config/pageDepthConfig';
import type { Device } from '@packages/types/device/dto';

const EMPTY_DEVICES: Device[] = [];

/**
 * HierarchicalSelectorContainer
 *
 * 비즈니스 로직을 담당하는 Container 컴포넌트:
 * - 데이터 fetching (companies, sites, devices)
 * - 권한별 필터링 (Admin, CompanyManager, SiteManager)
 * - 자동 선택 로직
 * - 전역 상태 관리 (selectionStore)
 */
interface HierarchicalSelectorContainerProps {
  overrideDepth?: SelectionDepth | null;
  /** Site 셀렉터에서 "All" 옵션을 숨기고 항상 특정 사이트가 선택되도록 강제 */
  disableSiteAll?: boolean;
}

export const HierarchicalSelectorContainer: React.FC<HierarchicalSelectorContainerProps> = ({
  overrideDepth,
  disableSiteAll = false,
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const {
    selectedCompanyId,
    selectedSiteId,
    selectedDeviceId,
    setSelectedCompanyId,
    setSelectedSiteId,
    setSelectedDeviceId,
  } = useSelectionStore();

  const {
    companies,
    sites,
    isAdmin,
    isCompanyManager,
    isSiteManager,
    loadingSites,
    isLoading: loadingCompanies,
    currentCompany,
  } = useCompanySiteScope();

  // isCompanyManager / isSiteManager는 hasRoleAtLeast라 Admin에도 true — UI 분기는 순수 역할만 사용
  const isSiteManagerOnly = isSiteManager && !isCompanyManager;

  // selectedSiteId를 직접 참조하여 site 변경 즉시 device 목록 반영
  const deviceQuerySiteId =
    selectedSiteId && selectedSiteId !== 'all' ? (selectedSiteId as number) : null;

  const { data: devices = EMPTY_DEVICES, isLoading: loadingDevices } = useGetSiteDevices(
    deviceQuerySiteId,
    {
      enabled: deviceQuerySiteId !== null,
    }
  );

  // site 변경 시 device 선택 초기화 (store의 자동 리셋을 보완)
  React.useEffect(() => {
    setSelectedDeviceId(null);
  }, [selectedSiteId]);

  // Company 자동 선택
  useEffect(() => {
    if (isAdmin && companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].id);
    }
    if (isCompanyManager && currentCompany && currentCompany.id !== selectedCompanyId) {
      setSelectedCompanyId(currentCompany.id);
    }
  }, [
    isAdmin,
    isCompanyManager,
    companies,
    currentCompany,
    selectedCompanyId,
    setSelectedCompanyId,
  ]);

  // SiteManager 사이트 자동 설정
  useEffect(() => {
    if (!isSiteManager) return;
    if (sites.length === 1 && selectedSiteId !== sites[0].id) {
      setSelectedSiteId(sites[0].id);
    } else if (sites.length > 1 && !selectedSiteId) {
      setSelectedSiteId('all');
    }
  }, [isSiteManager, sites, selectedSiteId, setSelectedSiteId]);

  // Site 자동 선택 (CompanyManager/Admin만, sites가 있고 선택이 없을 때)
  useEffect(() => {
    if (!isSiteManager && sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [isSiteManager, sites, selectedSiteId, setSelectedSiteId]);

  // disableSiteAll: 'all' 또는 미선택 상태면 첫 번째 사이트로 강제 선택
  useEffect(() => {
    if (!disableSiteAll) return;
    if (sites.length > 0 && (!selectedSiteId || selectedSiteId === 'all')) {
      setSelectedSiteId(sites[0].id);
    }
  }, [disableSiteAll, sites, selectedSiteId, setSelectedSiteId]);

  // 현재 페이지에 맞는 depth 가져오기
  const pageDepth = useMemo(() => {
    if (overrideDepth !== undefined) return overrideDepth;
    return getDepthForPath(location.pathname);
  }, [overrideDepth, location.pathname]);

  // HierarchicalSelector의 실제 depth 결정
  const depth = useMemo((): SelectionDepth => {
    if (pageDepth === null) {
      return 'company';
    }

    // Dashboard (/) - 모두 site까지 선택 가능
    if (location.pathname === '/') {
      return 'site';
    }

    // SiteManager는 최대 device까지만
    if (isSiteManager) {
      if (pageDepth === 'company') return 'company';
      if (pageDepth === 'site') return 'site';
      return 'device';
    }

    return pageDepth;
  }, [pageDepth, isSiteManager, location.pathname]);

  // HierarchicalSelector 표시 여부 (회사 목록이 비어 있거나 로딩 중이어도 영역은 보여 비활성/로딩 UI 표시)
  const showSelector = isAuthenticated && pageDepth !== null;

  if (!showSelector) {
    return null;
  }

  return (
    <HierarchicalSelector
      depth={depth}
      loadingCompanies={loadingCompanies}
      companies={companies}
      selectedCompanyId={selectedCompanyId}
      onCompanyChange={setSelectedCompanyId}
      companyDisabled={!isAdmin}
      showCompanyAll={isAdmin}
      sites={sites}
      selectedSiteId={selectedSiteId}
      onSiteChange={setSelectedSiteId}
      siteDisabled={isSiteManagerOnly && sites.length <= 1}
      loadingSites={loadingSites}
      showSiteAll={
        !disableSiteAll &&
        (isAdmin || (isAuthenticated && !isSiteManager) || (isSiteManager && sites.length > 1))
      }
      devices={devices}
      selectedDeviceId={selectedDeviceId}
      onDeviceChange={setSelectedDeviceId}
      loadingDevices={loadingDevices}
      showDeviceAll={false}
      gap="md"
    />
  );
};
